import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '../UI/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const { showSuccess, showError } = useToast();
  const [mode, setMode] = useState(initialMode); // 'login' or 'register'
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === 'login') {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          showSuccess(t('success.welcomeBack', { name: result.user.first_name }));
          onClose();
          setFormData({
            email: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            phone: ''
          });
        } else {
          showError(`${t('errors.loginFailed')}: ${result.error}`);
        }
      } else {
        // Registration
        if (formData.password !== formData.confirmPassword) {
          showError(t('errors.passwordsDontMatch'));
          return;
        }

        const result = await register({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          password_confirm: formData.confirmPassword
        });

        if (result.success) {
          showSuccess(t('success.accountCreated', { name: result.user.first_name }));
          onClose();
          setFormData({
            email: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            phone: ''
          });
        } else {
          // Use the improved error message from API
          showError(result.error || t('errors.registrationFailed') + '. ' + t('errors.tryAgain'));
        }
      }
    } catch (error) {
      const errorType = mode === 'login' ? t('errors.loginFailed') : t('errors.registrationFailed');
      showError(`${errorType}: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-sofia font-bold text-primary mb-2">
            {mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
          </h2>
          <p className="text-text-secondary font-sofia">
            {mode === 'login'
              ? t('auth.signInToAccount')
              : t('auth.joinCommunity')
            }
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                    {t('auth.firstName')}
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                    {t('auth.lastName')}
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                  {t('auth.phoneNumber')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('auth.phonePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                  required
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
              {t('auth.emailAddress')}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
              {t('auth.password')}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
              required
            />
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                {t('auth.confirmPassword')}
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                required
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
          >
            {isLoading
              ? (mode === 'login' ? t('auth.signingIn') : t('auth.creatingAccount'))
              : (mode === 'login' ? t('auth.signIn') : t('auth.createAccount'))
            }
          </Button>
        </form>

        {/* Switch mode */}
        <div className="text-center mt-6">
          <p className="text-text-secondary font-sofia">
            {mode === 'login' ? t('auth.noAccount') : t('auth.haveAccount')}
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-accent font-semibold hover:underline"
            >
              {mode === 'login' ? t('auth.signUp') : t('auth.signIn')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
